import axios, { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import React, { useCallback, useRef, useState } from 'react';
import { Button } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import getCrawler from 'api/Dataset/getCrawler';
import getDatasetPresignedUrl from 'api/Dataset/getDatasetPresignedUrl';
import startGlueCrawler from 'api/Dataset/startGlueCrawler';
import syncTables from 'api/Dataset/syncTables';

import TextInput from 'components/Text Inputs/TextInput';
import LinearProgressWithLabel from 'components/loader/LinearProgressWithLabel';
import SimpleLoader from 'components/loader/SimpleLoader';
import { useAlert } from 'components/message/AlertContext';
import useMounted from 'hooks/useMounted';
import { Dataset } from 'types/Dataset';

interface Message {
    label: string;
    positive: boolean;
}

interface UploaderProps {
    dataset: Dataset;
    client: any;
}

const Uploader: React.FC<UploaderProps> = ({ dataset, client }) => {
    const { openAlert } = useAlert();
    const mounted = useMounted();
    const [messages, setMessages] = useState<Message[]>([]);
    const [prefix, setPrefix] = useState<string>('raw');
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    let hiddenInputRef = useRef<HTMLInputElement>(null);

    const verifyCrawlerStatus = useCallback(
        async (name: string) => {
            try {
                setLoading(true);

                const interval = setInterval(async () => {
                    if (client && dataset.datasetUri) {
                        // polling for new crawler status every 10 seconds
                        const response = await client.query(
                            getCrawler({
                                datasetUri: dataset.datasetUri,
                                name,
                            }),
                        );
                        const crawlerObject = response.data.getCrawler;

                        openAlert({
                            message: 'Crawler is in progress, please wait.',
                            severity: 'info',
                        });

                        if (crawlerObject.status === 'SUCCEEDED') {
                            // close interval and polling after crawler finishes
                            setLoading(false);
                            await synchronizeTables();
                            clearInterval(interval);
                        }

                        if (!mounted.current) {
                            setLoading(false);
                            clearInterval(interval);
                        }
                    }
                }, 10000);
            } catch (error) {
                console.error(error);
                setLoading(false);
                openAlert({
                    message: `Failed to update crawler status.`,
                    severity: 'alert',
                });
            }
        },
        [client, dataset.datasetUri, getCrawler, mounted, openAlert],
    );

    const synchronizeTables = async () => {
        setLoading(true);
        try {
            const response = await client.mutate(
                syncTables(dataset.datasetUri),
            );
            if (!response.errors) {
                openAlert({
                    message:
                        'Synchronized tables successfully, check Tables tab',
                    severity: 'success',
                });
            } else {
                openAlert({
                    message: `Failed to synchronize tables from Glue catalog. ${response.errors[0].message}`,
                    severity: 'alert',
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const runCrawler = async () => {
        try {
            if (dataset.datasetUri) {
                const response = await client.mutate(
                    startGlueCrawler({
                        datasetUri: dataset.datasetUri,
                        input: { prefix: prefix },
                    }),
                );

                if (!response.errors) {
                    verifyCrawlerStatus(response.data.startGlueCrawler.Name);
                    openAlert({
                        message: 'Started Crawler',
                        severity: 'success',
                    });
                } else {
                    openAlert({
                        message: `Error while starting Crawler: ${response.errors[0].message}`,
                        severity: 'alert',
                    });
                }
            }
        } catch (error) {
            console.error(error);
            openAlert({
                message: 'Error while starting Crawler',
                severity: 'alert',
            });
        }
    };

    const fileUpload = async () => {
        setMessages([
            ...messages,
            { label: 'Starting upload', positive: true },
        ]);
        const response = await client.query(
            getDatasetPresignedUrl({
                datasetUri: dataset.datasetUri,
                input: {
                    fileName: file?.name || '',
                    prefix: prefix,
                },
            }),
        );
        if (!response.errors) {
            const presignedUrlResponse = JSON.parse(
                response.data.getDatasetPresignedUrl,
            );
            const url = presignedUrlResponse.url;
            const fields = presignedUrlResponse.fields;
            const formData = new FormData();
            Object.keys(fields).forEach((formFieldName) => {
                formData.append(formFieldName, fields[formFieldName]);
            });
            formData.append('file', file as File);

            const config: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Access-Control-Allow-Methods':
                        'GET, POST, PUT, DELETE, PATCH, OPTIONS',
                    'Access-Control-Allow-Headers':
                        'X-Requested-With, content-type, Authorization',
                },
                withCredentials: false,
                onUploadProgress: (e: AxiosProgressEvent) => {
                    setProgress(
                        Math.round((e.loaded * 100) / (e.total || 100)),
                    );
                },
            };

            setIsUploading(true);
            await axios.post(url, formData, config);
            setTimeout(() => {
                setIsUploading(false);
                setFile(null);
            }, 1000);

            openAlert({
                message: 'Table correctly uploaded',
                severity: 'success',
            });

            if (dataset.enableCrawler) {
                await runCrawler();
            } else {
                openAlert({
                    message:
                        'Crawler could not be started because is disabled. Please click EDIT on the upper right corner to enable it',
                    severity: 'alert',
                });
            }
        } else {
            openAlert({
                message: 'Something went wrong while trying to upload the file',
                severity: 'alert',
            });
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setFile(e.target.files[0]);
    };

    const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault(); // Stop form submit
        if (file) fileUpload();
    };

    return (
        <div>
            {isUploading && (
                <LinearProgressWithLabel value={progress} color="success" />
            )}
            {loading ? (
                <div>
                    <b>Crawler is in progress</b>
                    <SimpleLoader />
                </div>
            ) : (
                <form onSubmit={onFormSubmit}>
                    <b>
                        Crawler is{' '}
                        {dataset.enableCrawler ? 'enabled' : 'disabled'}
                    </b>
                    <div className="grid grid-flow-col justify-stretch gap-4 mt-2">
                        <div>
                            <TextInput
                                fullWidth={true}
                                helperText="Prefix without trailing slash at the end (e.g raw not raw/)"
                                startAdornment={`s3://${dataset.targetS3BucketName}/`}
                                value={prefix}
                                handleChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ): void => {
                                    setPrefix(e.target.value);
                                }}
                                label="Choose S3 bucket prefix"
                            />
                        </div>
                        <TextInput
                            fullWidth={true}
                            value={file?.name || ''}
                            label="Choose files to upload"
                        />
                        <input
                            ref={hiddenInputRef}
                            type="file"
                            hidden
                            onChange={onChange}
                            id={`file`}
                        />
                        <div className="place-self-center">
                            {file ? (
                                <div className="gap-4 flex">
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setFile(null);
                                            hiddenInputRef.current?.click();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Upload file
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    onClick={() => {
                                        hiddenInputRef.current?.click();
                                    }}
                                    component="label"
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    disabled={!dataset.enableCrawler}
                                >
                                    Select file
                                </Button>
                            )}
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

export default Uploader;
